<script lang="ts">
    import { _ } from 'svelte-i18n';
    import Switch from '../../shared/Switch.svelte';
    import SettingsRow from '../Settings/SettingsRow.svelte';
    import SettingsRowLabel from '../Settings/SettingsRowLabel.svelte';
    import SettingsRowValue from '../Settings/SettingsRowValue.svelte';
    import { app, type PermissionsType } from '../../../stores/app';
    import { permissionSummaryKey, permissionSummaryLabel } from '../../shared/Permissions/permissionSummary';
    import { permissionErrorKey } from '../UserPermissions/UserPermissionsApi';

    /**
     * Przełącznik roli user_manager w rozwiniętym wierszu konta (US-018).
     *
     * Wiersz jest osobnym komponentem, a nie kolejnym blokiem w AccountOne.svelte, bo dzięki temu
     * całe zachowanie (widoczność dla admina, cofanie po odmowie, komunikat błędu) da się
     * przetestować bez budowania Account, AccountApp i store'a api.
     *
     * Zapis jest wstrzykiwany przez onSave i to on jest źródłem prawdy o stanie po zmianie:
     * nadanie roli wymusza w backendzie can_read i can_write (Admin::setUserManager), więc front
     * nie zgaduje wynikowych flag.
     */
    export let webuserId: number | null;
    export let permissions: PermissionsType | null;
    export let onSave: (isUserManager: boolean) => Promise<PermissionsType>;
    export let onSaved: (permissions: PermissionsType) => void = () => {};

    let saving: boolean = false;
    let errorKey: string = '';

    // stan wyjściowy bierzemy z konta; po udanym zapisie rodzic dostaje ten sam obiekt przez onSaved
    $: current = permissions;
    $: checked = current?.isUserManager === true;

    /**
     * Rola user_manager nie ma sensu bez znanego webuser_id ani bez wczytanych flag uprawnień -
     * przełącznik bez stanu wyjściowego pokazywałby domysł, a jego kliknięcie zmieniałoby
     * uprawnienia w nieznaną stronę.
     */
    $: available = $app.isAdmin && webuserId !== null && current !== null;

    async function handleChange(next: boolean): Promise<void> {
        if (webuserId === null) {
            return;
        }

        const previous = current;
        saving = true;
        errorKey = '';
        try {
            current = await onSave(next);
            onSaved(current);
        } catch (error) {
            console.warn('Cannot change user_manager role', error);
            errorKey = permissionErrorKey(error);
            current = previous;
            // cofamy sam przełącznik - `checked` przelicza się tylko przy zmianie `current`,
            // a ten po odmowie wraca do wartości sprzed kliknięcia
            checked = previous?.isUserManager === true;
        } finally {
            saving = false;
        }
    }
</script>

{#if available}
    <SettingsRow>
        <SettingsRowLabel>
            {$_(permissionSummaryLabel)}
        </SettingsRowLabel>
        <SettingsRowValue>
            <div class="user-manager">
                <Switch
                    bind:checked
                    label={$_('User manager')}
                    disabled={saving}
                    on:change={event => {
                        console.log('Switch change event', event, checked);
                        void handleChange(checked !== true); // stan przełącznika jest przed zmianą
                    }}
                />
                <span class="state">
                    {current === null ? $_('No data') : $_(permissionSummaryKey(current) ?? 'No data')}
                </span>
            </div>
            {#if errorKey !== ''}
                <div class="error">{$_(errorKey)}</div>
            {/if}
        </SettingsRowValue>
    </SettingsRow>
{/if}

<style>
    .user-manager {
        display: flex;
        align-items: center;
        column-gap: 15px;
    }
    .state {
        color: #777;
    }
    .error {
        color: var(--error-color, #d32f2f);
        padding-top: 5px;
    }
</style>
